<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useSessionStore } from '@/stores/session.store';
import { useResourceSummaryStore } from '@/stores/resourceSummary.store';
import { useTheHoSignalR } from '@/composables/useTheHoSignalR';
import AppLayout from '@/views/layouts/AppLayout.vue';
import InstallPrompt from '@/components/shared/InstallPrompt.vue';
import UpdatePrompt from '@/components/shared/UpdatePrompt.vue';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const session = useSessionStore();
const resourceSummary = useResourceSummaryStore();
const signalR = useTheHoSignalR();

// 'game' = layout joueur (header + sidebar gauche + bottom-nav mobile).
// 'admin' = layout back-office (rendu via le parent router AdminLayout — pas
//           d'AppLayout autour). Tout autre = page nue (login, landing, …).
// Le layout de jeu n'est utilisé QUE pour un visiteur authentifié : une route
// 'game' publique (le forum en lecture) ouverte à un anonyme rend une coquille
// publique légère (cf publicGameRoute) au lieu de l'AppLayout (qui suppose une
// session).
const useGameLayout = computed(() => route.meta?.layout === 'game' && auth.isAuthenticated);
const publicGameRoute = computed(() => route.meta?.layout === 'game' && !auth.isAuthenticated);

onMounted(async () => {
  if (auth.isAuthenticated && !auth.user) {
    await auth.loadMe();
  }

  // Démarre la connexion SignalR — auto-connecte si déjà authentifié,
  // se déconnecte au logout, branche les stores via signalRStoreManager.
  await signalR.init();

  // Sur 401 emis par l'intercepteur axios : purge + redirection
  window.addEventListener('theho:unauthorized', () => {
    auth.clearSession();
    session.clear();
    resourceSummary.clear();
    if (route.meta?.requiresAuth) {
      router.push({ name: 'login' });
    }
  });
});
</script>

<template>
  <div class="app-root">
    <AppLayout v-if="useGameLayout" />
    <!-- Route 'game' publique (forum en lecture) vue par un anonyme : coquille
         légère (marque + connexion) au lieu de l'AppLayout. -->
    <div v-else-if="publicGameRoute" class="public-game-shell">
      <header class="pgs-bar">
        <router-link to="/welcome" class="pgs-brand">← The H'O</router-link>
        <router-link to="/login" class="pgs-login">Se connecter</router-link>
      </header>
      <main class="pgs-main"><RouterView /></main>
    </div>
    <RouterView v-else />

    <!-- PWA : toasts d'install + de mise à jour. Globaux, donc montés en
         dehors du layout pour rester visibles sur toutes les routes. -->
    <InstallPrompt />
    <UpdatePrompt />
  </div>
</template>

<style lang="scss" scoped>
.app-root {
  min-height: 100vh;
}

// Coquille publique légère pour le forum consulté sans compte.
.public-game-shell {
  min-height: 100vh;
  background: $bg-primary;
  color: $text-primary;
}
.pgs-bar {
  display: flex; align-items: center; justify-content: space-between;
  height: 56px; padding: 0 $space-5;
  background: rgba(11, 18, 32, 0.85);
  border-bottom: 1px solid $border-default;
  position: sticky; top: 0; z-index: 10;
}
.pgs-brand {
  font-family: $font-display; font-weight: $font-weight-bold; letter-spacing: 0.08em;
  color: $text-primary; text-decoration: none;
  &:hover { color: $color-neon-cyan; }
}
.pgs-login {
  color: $accent-primary; text-decoration: none; font-size: $font-size-sm; font-weight: 500;
  &:hover { text-decoration: underline; }
}
.pgs-main { max-width: $layout-max-width; margin: 0 auto; padding: $space-5; }
</style>
